import { Select } from '@mantine/core';
import { CityRO } from '@routellers/types/dist/city/city.ro';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api';
import { parseCityFilter } from '../utils/roadmap-cities';

type Props = {
    value: string;
    onChange: (value: string) => void;
};

const ALL = 'all';

export const CityQuickSelect = ({ value, onChange }: Props) => {
    const { t } = useTranslation();
    const [cities, setCities] = useState<CityRO[]>([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const { data } = await api.cities.query({
                    page: 1,
                    limit: 500,
                    sort: 'id',
                    order: 'ASC',
                    where: { readyForRoutell: null } as unknown as Record<string, never>,
                });

                if (!cancelled) setCities(data?.items || []);
            } catch {
                if (!cancelled) setCities([]);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const data = useMemo(() => {
        const groups = cities.reduce<Record<string, { value: string; label: string }[]>>(
            (acc, city) => {
                const group = t(`enums.countries.${city.country}`, {
                    defaultValue: city.country,
                });

                if (!acc[group]) acc[group] = [];
                acc[group].push({
                    value: String(city.id),
                    label: `${city.title} · #${city.id}`,
                });

                return acc;
            },
            {},
        );

        return [
            { value: ALL, label: t('components.cityQuickSelect.allCities') },
            ...Object.entries(groups).map(([group, items]) => ({ group, items })),
        ];
    }, [cities, t]);

    return (
        <Select
            w={260}
            data={data}
            value={value}
            onChange={(next) => onChange(next || ALL)}
            searchable
            nothingFoundMessage={t('components.cityQuickSelect.nothingFound')}
            placeholder={t('components.cityQuickSelect.placeholder')}
            clearable={false}
        />
    );
};

export { parseCityFilter };
